import { ActionIcon } from "PFComponents/action_icon";
import SectionBox from "PFComponents/box/section_box";
import { Button } from "PFComponents/button";
import Modal from "PFComponents/modal/modal";
import { Typography } from "PFComponents/typography";
import { ImportStatus, useDataImportStatus } from "PFCore/hooks/queries/admin/use_data_import_status";
import { Id, LocalisationKeys } from "PFTypes";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "../localisation_export.module.scss";
import { ImportInProgress } from "./import_in_progress";
import { ImportSummary } from "./import_summary";
import { UploadFile } from "./upload_file";

type ImportModalProps = {
  accountId?: Id;
  onClose?: () => void;
  onExportModalOpen: () => void;
  localisationKey: LocalisationKeys;
};

const isInProgress = (importStatus?: ImportStatus | null): boolean =>
  !!importStatus && ["created", "processing"].includes(importStatus.state);
const isFinished = (importStatus?: ImportStatus | null): boolean =>
  !!importStatus && ["completed", "failed"].includes(importStatus.state);

export const ImportModal = ({ onClose, accountId, localisationKey, onExportModalOpen }: ImportModalProps) => {
  const { t } = useTranslation(["admin", "translation"]);

  const [fileUploadData, setFileUploadData] = useState<ImportStatus | null>(null);

  const { data: importData, isFetching: isAnalysing } = useDataImportStatus(
    accountId || 0,
    Number(fileUploadData?.id),
    {
      onSuccess: (status) => {
        if (isFinished(status)) {
          setFileUploadData(status);
        }
      },
      enabled: isInProgress(fileUploadData),
      refetchInterval: fileUploadData ? 1000 : false,
      refetchIntervalInBackground: true
    }
  );

  const renderImportContent = () => {
    if (isInProgress(importData) || isAnalysing) {
      return <ImportInProgress onClose={onClose} />;
    }

    if (isFinished(importData)) {
      return (
        <ImportSummary
          localisationKey={localisationKey}
          processedRows={fileUploadData?.summary?.processedRows}
          fileErrors={fileUploadData?.summary?.fileErrors}
        />
      );
    }

    return (
      <UploadFile
        accountId={accountId}
        localisationKey={localisationKey}
        onExportModalOpen={onExportModalOpen}
        onUpload={setFileUploadData}
      />
    );
  };

  return (
    //@ts-ignore-line
    <Modal closeOnBackdropClick handleClose={onClose} classes={{ dialog: css.importModal }}>
      <SectionBox className={css.modal}>
        <section className={css.header}>
          <Typography variant="h4" noMargin>
            {t(`admin:localisation.importModal.header.${localisationKey}`)}
          </Typography>
          <ActionIcon name="cross" size="sm" onClick={onClose} />
        </section>
        {renderImportContent()}
        <section className={css.footer}>
          <Button kind="secondary" text={t("translation:cancel")} onClick={onClose} />
          {isFinished(importData) && (
            <Button text={t("admin:localisation.importModal.allDone")} onClick={onClose} />
          )}
        </section>
      </SectionBox>
    </Modal>
  );
};
